import { AccountsApi, AuthenticationApi, MessagesApi, UserApi, VendorsApi, OauthApi } from "@sb1finans/new-api/dist"
import React, { createContext, FC, useEffect, useMemo, useState, useContext } from "react"
import axios, { AxiosError, AxiosResponse } from "axios";


const createGenericContext = <T extends unknown>() => {

    const genericContext = createContext<T | undefined>(undefined)

    const Provider: FC<React.PropsWithChildren<{ value: T }>> = ({ value, children }) => {
        return <genericContext.Provider value={value}>{children}</genericContext.Provider>
    }

    const Consumer = () => {
        const context = useContext(genericContext)
        if (!context) {
            throw new Error('useGenericContext must be used within a Provider')
        }
        return context
    }

    return [ Consumer, genericContext.Provider ] as const
}

interface ApiContextProps {
    vendorsApi: VendorsApi
    authApi: AuthenticationApi
    userApi: UserApi
    accountsApi: AccountsApi
    messagesApi: MessagesApi
    oauthApi: OauthApi
    error?: Error
}

const [ useApiContext, ApiContextProvider ] = createGenericContext<ApiContextProps>()

interface ApiProviderProps {
    config: any
}

export const ApiProvider: React.FC<ApiProviderProps> = ({children, config}) => {

    const [error, setError] = useState<Error | undefined>(undefined)

    // Fetch token from somewhere
    const searchParams = new URLSearchParams(window.location.search)
    const tokenParam = useMemo(() => searchParams.get("authToken"), [searchParams])
    const hasToken = tokenParam && tokenParam.length > 20
    if (hasToken) {
        // Set token on local storage
        localStorage.setItem("auth_token", tokenParam)
        const url = new URL(window.location.href)
        url.searchParams.delete("authToken")
        window.location.href = url.toString()
    }
    console.log("IN API PROVIDER", error)


    console.info("Has token", hasToken)
    const axiosInstance = axios.create({
        baseURL: config.apiUrl,
        timeout: 10000,
        withCredentials: hasToken ? false : true
    })

    const vendorsApi = new VendorsApi(undefined, config.apiUrl, axiosInstance)
    const authApi = new AuthenticationApi(undefined, config.apiUrl, axiosInstance)
    const userApi = new UserApi(undefined, config.apiUrl, axiosInstance)
    const messagesApi = new MessagesApi(undefined, config.apiUrl, axiosInstance)
    const accountsApi = new AccountsApi(undefined, config.apiUrl, axiosInstance)
    const oauthApi = new OauthApi(undefined, config.apiUrl, axiosInstance)

    useEffect(() => {
        const requestInterceptor = (request: any) => {
            const token = localStorage.getItem("auth_token")
            if (!token) return request
            console.info("Attaching token to request", token)
            request.headers = {
                ...request.headers,
                'Authorization': `Bearer ${token}`
            }
            return request
        }
        const responseInterceptor = (response: AxiosResponse) => {
            return response;
        }
        const errorInterceptor = (err: Error) => {
            if (err instanceof AxiosError) {
                const status = err.response?.status
                // If we get a 401 and the token has changed. Retry with new token.
                if (status === 401) {
                    console.error("Unauthorized, clearing token")
                    localStorage.removeItem("auth_token")
                    setError(err)
                    return Promise.reject(err);
                } else {
                    setError(err)
                    return Promise.reject(err);
                }
            }
        }
        const responseInterceptorId = axiosInstance.interceptors.response.use(responseInterceptor, errorInterceptor)
        const requestInterceptorId = axiosInstance.interceptors.request.use(requestInterceptor)

        return () => {
            axiosInstance.interceptors.response.eject(responseInterceptorId)
            axiosInstance.interceptors.request.eject(requestInterceptorId)
        }
    }, [])

    console.log("ERROR", error)
    return (
        <ApiContextProvider value={{vendorsApi, authApi, userApi, messagesApi, accountsApi, oauthApi, error}}>
            {children}
        </ApiContextProvider>
    )
}

export default useApiContext

export const ApiContext = ApiContextProvider